import { useTranslation } from 'react-i18next';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, Row, theme, Typography } from 'antd';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import type {
  OpeningHoursGroupModel,
  TradingTimesModel,
} from 'pos-shared/models/openingHoursGroupSchema.ts';

import { LocationHourGroup } from './LocationHourGroup.tsx';
import { mapLocationHours } from './LocationHours.tsx';

interface LocationHoursModalProps {
  openingHoursGroups: OpeningHoursGroupModel[] | undefined;
  currentOpeningHoursGroup: TradingTimesModel[];
  currentOpeningHoursGroupName: string | undefined;
  hideModal: () => void;
  handleOpeningHoursGroupAssign: () => void;
  isModalOpen: boolean;
  isUpdating: boolean;
  selectedOpeningHoursGroupId: string;
}

export const LocationHoursModal = ({
  openingHoursGroups,
  currentOpeningHoursGroup,
  currentOpeningHoursGroupName,
  hideModal,
  handleOpeningHoursGroupAssign,
  isModalOpen,
  isUpdating,
  selectedOpeningHoursGroupId,
}: LocationHoursModalProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const findOpeningHoursGroupById = (id: string) => {
    return openingHoursGroups?.find((group) => group.id === id);
  };

  const selectedHoursGroup = findOpeningHoursGroupById(
    selectedOpeningHoursGroupId
  );

  const selectedHoursGroupData =
    (selectedHoursGroup?.tradingTimes &&
      mapLocationHours(selectedHoursGroup.tradingTimes)) ??
    [];

  return (
    <PosModal
      header={{ title: t('locations.trading.confirmNewHours'), icon: faClock }}
      open={isModalOpen}
      onCancel={hideModal}
      width={token.screenXL}
      footer={null}
    >
      <Row
        gutter={token.marginLG}
        style={{ paddingTop: token.marginXL, paddingBottom: token.marginXL }}
        justify="center"
        align="middle"
      >
        <Col span="11">
          <LocationHourGroup
            groupName={currentOpeningHoursGroupName ?? ''}
            groupHours={currentOpeningHoursGroup}
            color={'gold'}
            changeFrom
          />
        </Col>
        <Col span="2" style={{ textAlign: 'center' }}>
          <FontAwesomeIcon size="2x" icon={faArrowRight} />
        </Col>
        <Col span="11">
          <LocationHourGroup
            groupName={selectedHoursGroup?.groupName ?? ''}
            groupHours={selectedHoursGroupData}
            color={'green'}
          />
        </Col>
      </Row>
      <Row
        gutter={token.marginLG}
        style={{
          paddingTop: token.marginXL,
          paddingBottom: token.marginXL,
          flexDirection: 'column',
        }}
        justify="center"
        align="middle"
      >
        <Col style={{ textAlign: 'center' }}>
          <Typography.Text style={{ display: 'block' }}>
            {t('locations.hoursChangeMessage')}
          </Typography.Text>
          <Typography.Text>{t('locations.confirmHoursChange')}</Typography.Text>
        </Col>
        <Col style={{ paddingTop: token.paddingMD }}>
          <Button style={{ marginRight: token.padding }} onClick={hideModal}>
            {t('common.buttons.cancel')}
          </Button>
          <Button
            type="primary"
            loading={isUpdating}
            onClick={handleOpeningHoursGroupAssign}
          >
            {t('common.buttons.confirm')}
          </Button>
        </Col>
      </Row>
    </PosModal>
  );
};
